import { FC, useMemo } from 'react';
import { UiOption } from '@/lib/helpers';
import Chip from '@/components/chip';
import ChipSelect from '@/components/chip-select';

interface Props {
  value: string;
  options: UiOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  readonly?: boolean;
}

const NO_DOMAINS_OPTION: UiOption = {
  value: 'No Domains Available',
  label: 'No Domains Available',
};

const DomainChipSelect: FC<Props> = ({
  value = '',
  options,
  onChange,
  disabled = false,
  readonly = false,
}) => {
  const hasMultipleDomains = options.length > 1;
  const validValues = useMemo(() => options.map((opt) => opt.value), [options]);
  const isSelectedInvalid = !validValues.includes(value);

  const domainOptions = useMemo(() => {
    const hasOptions = !!options.length;
    const selectedInvalid = value && !validValues.includes(value);
    const optionsToUse = hasOptions ? options : [NO_DOMAINS_OPTION];
    if (!selectedInvalid) {
      return optionsToUse;
    }

    const invalidOption: UiOption = {
      value,
      label: <span>{`@${value}`}</span>,
      meta: {
        endAdornment: <Chip label="Invalid Domain" color="error" size="x-small" />,
      },
    };

    return [invalidOption, ...optionsToUse];
  }, [options, value, validValues]);

  const handleChange = (domainValue: string) => {
    onChange(domainValue);
  };

  // When selected domain is invalid, allow opening dropdown so user can pick a valid one
  const compReadOnly = isSelectedInvalid ? false : readonly || !hasMultipleDomains;

  return (
    <ChipSelect
      name="domain-chip-select"
      value={value}
      options={domainOptions}
      onChange={handleChange}
      disabled={disabled}
      readonly={compReadOnly}
      isError={!!isSelectedInvalid}
      size="medium"
    />
  );
};

export default DomainChipSelect;
